import authService from './authService';
import {
  DashboardsResponse,
  DashboardResponse,
  AddWidgetRequest,
  UpdateLayoutRequest
} from '@/types/dashboard';

const API_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_URL) {
  throw new Error('VITE_API_BASE_URL environment variable is not set');
}

class DashboardService {
  /**
   * Create a new dashboard
   */
  async createDashboard(title: string): Promise<DashboardResponse> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ title })
    });

    if (!response.ok) {
      throw new Error(`Failed to create dashboard: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get all dashboards for current user
   */
  async getDashboards(): Promise<DashboardsResponse> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load dashboards: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a dashboard with all widgets
   */
  async getDashboard(dashboardId: string): Promise<DashboardResponse> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load dashboard: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update a dashboard title
   */
  async updateDashboard(dashboardId: string, title: string): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ title })
    });

    if (!response.ok) {
      throw new Error(`Failed to update dashboard: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Toggle dashboard visibility
   */
  async toggleVisibility(dashboardId: string, isPublic: boolean): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}/visibility`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ isPublic })
    });

    if (!response.ok) {
      throw new Error(`Failed to toggle visibility: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a dashboard
   */
  async deleteDashboard(dashboardId: string): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete dashboard: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Add a widget to a dashboard
   */
  async addWidget(dashboardId: string, widgetData: AddWidgetRequest): Promise<{ success: boolean; widget: any }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}/widgets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(widgetData)
    });

    if (!response.ok) {
      throw new Error(`Failed to add widget: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update a widget
   */
  async updateWidget(
    dashboardId: string,
    widgetId: string,
    data: { layout?: any; widgetConfig?: any; title?: string }
  ): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}/widgets/${widgetId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`Failed to update widget: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Remove a widget from dashboard
   */
  async removeWidget(dashboardId: string, widgetId: string): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}/widgets/${widgetId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to remove widget: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Bulk update widget layouts
   */
  async updateLayout(dashboardId: string, layoutsData: UpdateLayoutRequest): Promise<{ success: boolean }> {
    const tokens = authService.getStoredTokens();
    
    if (!tokens) {
      throw new Error('Not authenticated');
    }
    
    const token = tokens.accessToken;

    const response = await fetch(`${API_URL}/api/admin/dashboards/${dashboardId}/layout`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(layoutsData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update layout: ${response.statusText}`);
    }

    return response.json();
  }
}

export default new DashboardService();
